import type { ReactNode } from 'react';
import { cx } from '../../lib/util/cx';
import type { AddressShape } from '../dishes/api';
import { SectionLabel } from '../profiles/people';

/**
 * Layout pieces shared by the Dish Detail and Order Detail panels: labelled
 * fields in a grid, a money ledger, address and key/value formatting.
 */

export function Section({ title, children, className }: { title: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx('mt-[18px]', className)}>
      <SectionLabel>{title}</SectionLabel>
      {children}
    </section>
  );
}

export function FieldGrid({ children }: { children: ReactNode }) {
  return <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">{children}</dl>;
}

/** A label over a value. `wide` spans both grid columns. Empty values render "—". */
export function Field({ label, children, wide }: { label: ReactNode; children?: ReactNode; wide?: boolean }) {
  const empty = children === undefined || children === null || children === '' || children === false;
  return (
    <div className={cx('min-w-0', wide && 'col-span-2')}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="m-0 mt-0.5 break-words">{empty ? <span className="text-muted">—</span> : children}</dd>
    </div>
  );
}

/** Label / right-aligned amount rows; `total` rows are bold with a rule above. */
export function Ledger({
  rows,
}: {
  rows: Array<{ label: ReactNode; value: ReactNode; hint?: ReactNode; total?: boolean }>;
}) {
  return (
    <div className="text-sm">
      {rows.map((r, i) => (
        <div
          key={i}
          className={cx(
            'flex items-baseline justify-between gap-4 py-1.5',
            r.total ? 'border-t border-[rgba(143,68,32,.14)] font-semibold' : 'border-t border-[rgba(143,68,32,.06)]',
          )}
        >
          <span>
            {r.label}
            {r.hint && <span className="ml-2 text-xs font-normal text-muted">{r.hint}</span>}
          </span>
          <span className="tabular-nums">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

export function formatAddress(a: AddressShape | null | undefined): string {
  if (!a) return '';
  if (a.address) return a.address;
  const cityLine = [a.city, [a.stateProvince, a.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [a.street1, a.street2, cityLine, a.country].filter(Boolean).join(', ');
}

/** Free-form JSON objects (time windows, ingredients, cancellation breakdowns) as key / value lines. */
export function KeyValues({ value }: { value: Record<string, unknown> | null | undefined }) {
  const entries = Object.entries(value ?? {});
  if (!entries.length) return null;
  return (
    <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <span className="text-muted">{k}</span>
          <span className="min-w-0 break-words font-mono text-xs leading-5">
            {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function isEmptyObject(value: Record<string, unknown> | null | undefined): boolean {
  return !value || Object.keys(value).length === 0;
}

export function tagList(tags: string[] | null | undefined): string {
  return (tags ?? []).join(', ');
}
